<template>
  <div class="chart-container">
    <div class="chart-header">
      <div class="chart-title">{{ chartTitle }}</div>
      <div class="chart-switch">
        <span class="switch-text" @click="toggleChartType">切换</span>
      </div>
    </div>
    <div id="lineChart" style="width: 100%; height: calc(100% - 20px)"></div>

    <div v-if="dialogVisible" class="modal-overlay" @click="closeDialog">
      <div
        class="modal-content"
        :class="{ 'is-detail-modal': chartType === 'order' || chartType === 'payment' }"
        @click.stop
      >
        <div class="modal-header">
          <h3>{{ dialogTitle }}</h3>
          <span class="close-btn" @click="closeDialog">&times;</span>
        </div>

        <div class="modal-body">
          <div class="dialog-search">
            <input
              v-model="searchKeyword"
              type="text"
              class="search-input"
              :placeholder="searchPlaceholder"
              @input="handleSearchChange"
            />
          </div>

          <!-- 缴费记录 -->
          <template v-if="chartType === 'payment'">
            <div class="data-table fee-table">
              <div class="table-header fee-header">
                <div class="header-item col-community">小区名称</div>
                <div class="header-item col-owner">业主名称</div>
                <div class="header-item col-year">缴费时间</div>
                <div class="header-item col-content">缴费内容</div>
                <div class="header-item col-amount">缴费金额</div>
                <div class="header-item col-status">缴费状态</div>
                <div class="header-item col-create">创建时间</div>
              </div>
              <div class="table-body fee-body">
                <div
                  v-for="(item, index) in pagedTableData"
                  :key="`fee-${index}`"
                  class="table-row fee-row"
                >
                  <div class="table-cell col-community text-left">{{ item.community_name || '-' }}</div>
                  <div class="table-cell col-owner text-left">{{ item.owner_name || '-' }}</div>
                  <div class="table-cell col-year">{{ item.create_day || '-' }}</div>
                  <div class="table-cell col-content text-left">{{ item.pay_content || '-' }}</div>
                  <div class="table-cell col-amount">{{ formatAmount(item.amount) }}</div>
                  <div class="table-cell col-status">{{ item.pay_status || '-' }}</div>
                  <div class="table-cell col-create">{{ item.createTime || '-' }}</div>
                </div>
                <div v-if="!pagedTableData.length" class="empty-tip">暂无数据</div>
              </div>
            </div>
          </template>

          <!-- 工单记录 -->
          <template v-else>
            <div class="data-table order-table">
              <div class="table-header order-header">
                <div class="header-item col-address">项目地址</div>
                <div class="header-item col-type">记录类型</div>
                <div class="header-item col-level">工单等级</div>
                <div class="header-item col-status">处理状态</div>
                <div class="header-item col-event">工单事件</div>
                <div class="header-item col-repair">维修描述</div>
                <div class="header-item col-images">工单图片</div>
                <div class="header-item col-images">维修图片</div>
                <div class="header-item col-time">生成时间</div>
              </div>
              <div class="table-body order-body">
                <div
                  v-for="(item, index) in pagedTableData"
                  :key="`order-${index}`"
                  class="table-row order-row"
                >
                  <div class="table-cell col-address text-left">{{ item.communityAddress || '-' }}</div>
                  <div class="table-cell col-type">{{ item.orderType || '-' }}</div>
                  <div class="table-cell col-level">
                    <span :class="getLevelClass(item.level)">{{ item.level || '-' }}</span>
                  </div>
                  <div class="table-cell col-status">
                    <span :class="getStatusClass(item.status)">{{ item.status || '-' }}</span>
                  </div>
                  <div class="table-cell col-event text-left">{{ item.orderEvent || '-' }}</div>
                  <div class="table-cell col-repair text-left">{{ item.repairContent || '-' }}</div>
                  <div class="table-cell col-images image-cell">
                    <template v-if="getImagesArray(item.orderImage).length">
                      <img
                        v-for="(img, imgIndex) in getImagesArray(item.orderImage)"
                        :key="`order-img-${imgIndex}`"
                        :src="img"
                        alt="工单图片"
                        class="table-image"
                        @click="openImagePreview(img)"
                      />
                    </template>
                    <span v-else>-</span>
                  </div>
                  <div class="table-cell col-images image-cell">
                    <template v-if="getImagesArray(item.repairImage).length">
                      <img
                        v-for="(img, imgIndex) in getImagesArray(item.repairImage)"
                        :key="`repair-img-${imgIndex}`"
                        :src="img"
                        alt="维修图片"
                        class="table-image"
                        @click="openImagePreview(img)"
                      />
                    </template>
                    <span v-else>-</span>
                  </div>
                  <div class="table-cell col-time">{{ item.createTime || '-' }}</div>
                </div>
                <div v-if="!pagedTableData.length" class="empty-tip">暂无数据</div>
              </div>
            </div>
          </template>

          <div v-if="filteredTableData.length" class="pagination-bar">
            <button
              class="page-btn"
              :disabled="pagination.currentPage <= 1"
              @click="handleCurrentChange(pagination.currentPage - 1)"
            >
              上一页
            </button>
            <span class="page-info">
              第 {{ pagination.currentPage }} / {{ totalPages }} 页，共 {{ filteredTableData.length }} 条
            </span>
            <button
              class="page-btn"
              :disabled="pagination.currentPage >= totalPages"
              @click="handleCurrentChange(pagination.currentPage + 1)"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showImagePreview" class="image-preview-overlay" @click="closeImagePreview">
      <div class="image-preview-content" @click.stop>
        <img :src="previewImageUrl" class="preview-image" alt="预览图片" />
        <span class="close-preview-btn" @click="closeImagePreview">&times;</span>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {
  filterFeeDetailsByItem,
  filterOrderDetailsByItem,
  formatFeeAmount,
} from "@/config/homeChartConfig";

export default {
  name: "PaymentRecordsChart",
  props: {
    chartData: {
      type: Array,
      default: () => [],
    },
    orderData: {
      type: Array,
      default: () => [],
    },
    paymentDetailList: {
      type: Array,
      default: () => [],
    },
    orderDetailList: {
      type: Array,
      default: () => [],
    },
    defaultType: {
      type: String,
      default: "payment",
    },
  },
  data() {
    return {
      chartInstance: null,
      chartType: this.defaultType,
      dialogVisible: false,
      selectedItem: null,
      tableData: [],
      searchKeyword: "",
      showImagePreview: false,
      previewImageUrl: "",
      pagination: {
        currentPage: 1,
        pageSize: 10,
      },
    };
  },
  computed: {
    chartTitle() {
      return this.chartType === "payment"
        ? "每月自主缴费记录统计"
        : "工单/故障统计类型";
    },
    dialogTitle() {
      const category = this.selectedItem ? this.selectedItem.name : "";
      return category
        ? `${category} - 详细数据（${this.filteredTableData.length}条）`
        : "详细数据";
    },
    searchPlaceholder() {
      return this.chartType === 'payment'
        ? '搜索小区名称/业主名称/缴费时间'
        : '搜索项目地址/记录类型/处理状态/生成时间'
    },
    filteredTableData() {
      const keyword = this.searchKeyword.trim().toLowerCase()
      if (!keyword) return this.tableData

      if (this.chartType === 'payment') {
        return this.tableData.filter((item) => {
          const communityName = String(item.community_name || '').toLowerCase()
          const ownerName = String(item.owner_name || '').toLowerCase()
          const payDay = String(item.create_day || item.pay_year || '').toLowerCase()
          return (
            communityName.includes(keyword) ||
            ownerName.includes(keyword) ||
            payDay.includes(keyword)
          )
        })
      }

      return this.tableData.filter((item) => {
        const projectAddress = String(item.communityAddress || '').toLowerCase()
        const orderType = String(item.orderType || '').toLowerCase()
        const status = String(item.status || '').toLowerCase()
        const createTime = String(item.createTime || '').toLowerCase()
        return (
          projectAddress.includes(keyword) ||
          orderType.includes(keyword) ||
          status.includes(keyword) ||
          createTime.includes(keyword)
        )
      })
    },
    totalPages() {
      return Math.max(
        1,
        Math.ceil(this.filteredTableData.length / this.pagination.pageSize)
      );
    },
    pagedTableData() {
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
      const end = start + this.pagination.pageSize;
      return this.filteredTableData.slice(start, end);
    },
  },
  watch: {
    chartData: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
    orderData: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
    chartType() {
      this.updateChart();
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initChart();
      this.updateChart();
    });
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    if (this.chartInstance) {
      this.chartInstance.off("click", this.handleChartClick);
      this.chartInstance.dispose();
    }
  },
  methods: {
    initChart() {
      const chartDom = document.getElementById("lineChart");
      if (!chartDom) return;

      this.chartInstance = echarts.init(chartDom);
      this.chartInstance.on("click", this.handleChartClick);
    },
    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
    },
    toggleChartType() {
      const newType = this.chartType === "payment" ? "order" : "payment";
      this.chartType = newType;
      this.$emit("chart-type-change", newType);
    },
    getCurrentChartData() {
      return this.chartType === "payment" ? this.chartData : this.orderData;
    },
    formatAmount(value) {
      const formatted = formatFeeAmount(value)
      return formatted !== '' ? formatted : '-'
    },
    handleChartClick(params) {
      const currentData = this.getCurrentChartData();
      const item = currentData[params.dataIndex];
      if (!item) return;

      this.selectedItem = item;
      this.searchKeyword = "";
      this.pagination.currentPage = 1;
      this.pagination.pageSize = this.chartType === "order" ? 8 : 10;

      if (this.chartType === "payment") {
        this.tableData = filterFeeDetailsByItem(
          item.details,
          item,
          this.paymentDetailList
        );
      } else {
        this.tableData = filterOrderDetailsByItem(
          item.details,
          item,
          this.orderDetailList
        );
      }

      this.dialogVisible = true;
    },
    closeDialog() {
      this.dialogVisible = false;
      this.selectedItem = null;
      this.tableData = [];
      this.searchKeyword = "";
      this.closeImagePreview();
    },
    handleSearchChange() {
      this.pagination.currentPage = 1;
    },
    handleCurrentChange(page) {
      if (page < 1 || page > this.totalPages) return;
      this.pagination.currentPage = page;
    },
    getImagesArray(imageStr) {
      if (!imageStr) return [];
      return String(imageStr)
        .split(",")
        .map((url) => url.trim())
        .filter(Boolean);
    },
    openImagePreview(url) {
      this.previewImageUrl = url;
      this.showImagePreview = true;
    },
    closeImagePreview() {
      this.showImagePreview = false;
      this.previewImageUrl = "";
    },
    getLevelClass(level) {
      const classMap = {
        低: "tag-low",
        中: "tag-medium",
        高: "tag-high",
        紧急: "tag-urgent",
      };
      return classMap[level] || "";
    },
    getStatusClass(status) {
      const classMap = {
        待分配: "status-pending",
        待处理: "status-processing",
        已处理: "status-done",
      };
      return classMap[status] || "";
    },
    updateChart() {
      if (!this.chartInstance) return;

      const currentData = this.getCurrentChartData();
      if (!currentData.length) {
        this.chartInstance.clear();
        return;
      }

      const yAxisName =
        this.chartType === "payment" ? "缴费数量" : "工单/故障数量";
      const lineColor = this.chartType === "payment" ? "#00d4ff" : "#ffaa00";

      const option = {
        tooltip: {
          trigger: "axis",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          borderColor: lineColor,
          textStyle: { color: "#fff" },
          formatter: (params) => {
            const point = Array.isArray(params) ? params[0] : params
            if (!point) return ''
            const valueText =
              this.chartType === 'payment'
                ? formatFeeAmount(point.value)
                : point.value
            return `${point.name}: ${valueText || point.value}`
          },
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          top: "40px",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: currentData.map((item) => item.name),
          axisLine: { lineStyle: { color: lineColor } },
          axisLabel: { color: "#fff", fontSize: 10 },
        },
        yAxis: {
          type: "value",
          name: yAxisName,
          axisLine: { lineStyle: { color: lineColor } },
          axisLabel: { color: "#fff" },
          splitLine: {
            lineStyle: { color: `rgba(${this.hexToRgb(lineColor)}, 0.2)` },
          },
        },
        series: [
          {
            data: currentData.map((item) => item.value),
            type: "line",
            smooth: true,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: `${lineColor}66` },
                { offset: 1, color: `${lineColor}1A` },
              ]),
            },
            lineStyle: {
              color: lineColor,
              width: 2,
            },
            itemStyle: {
              color: lineColor,
              borderColor: "#fff",
              borderWidth: 2,
            },
          },
        ],
      };
      this.chartInstance.setOption(option, true);
    },
    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
            result[3],
            16
          )}`
        : "0, 212, 255";
    },
  },
};
</script>

<style scoped lang="less">
.chart-container {
  background: rgba(10, 25, 41, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
  box-sizing: border-box;
  height: 100%;
}

.chart-header {
  position: relative;
  margin-bottom: 10px;
}

.chart-title {
  font-size: 16px;
  color: #00d4ff;
  font-weight: bold;
  text-align: center;
  margin-top: 10px;
}

.chart-switch {
  position: absolute;
  right: 0;
  top: 0;
}

.switch-text {
  color: #00d4ff;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.3s;

  &:hover {
    color: #88c4ff;
  }
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  width: 92%;
  max-width: 960px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #0a1929 0%, #1a2332 50%, #0e2a42 100%);
  border: 1px solid rgba(0, 212, 255, 0.5);
  border-radius: 10px;
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.3);

  &.is-detail-modal {
    width: 98%;
    max-width: 1680px;
    max-height: 94vh;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);

  h3 {
    margin: 0;
    color: #00d4ff;
    font-size: 18px;
  }
}

.close-btn {
  color: #00d4ff;
  font-size: 24px;
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    color: #ff3366;
  }
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.is-detail-modal .modal-body {
  padding: 16px 20px 20px;
}

.dialog-search {
  margin-bottom: 14px;
}

.search-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  background: rgba(0, 212, 255, 0.08);
  border: 1px solid rgba(0, 212, 255, 0.35);
  border-radius: 6px;
  color: #e8f4ff;
  font-size: 14px;
  outline: none;

  &::placeholder {
    color: #88c4ff;
  }

  &:focus {
    border-color: rgba(0, 212, 255, 0.6);
    box-shadow: 0 0 8px rgba(0, 212, 255, 0.15);
  }
}

.data-table {
  border: 1px solid rgba(0, 212, 255, 0.25);
  border-radius: 8px;
  overflow: hidden;
}

.order-table,
.fee-table {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.fee-header,
.fee-row {
  display: grid;
  grid-template-columns:
    minmax(160px, 2fr)
    minmax(100px, 1.2fr)
    minmax(90px, 0.8fr)
    minmax(140px, 1.6fr)
    minmax(100px, 1fr)
    minmax(90px, 0.9fr)
    minmax(128px, 1fr);
  align-items: stretch;
}

.fee-body {
  flex: 1;
  min-height: 0;
  max-height: calc(94vh - 180px);
  overflow-y: auto;
  overflow-x: auto;
}

.fee-row {
  min-height: 52px;
}

.fee-table .header-item,
.fee-table .table-cell {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.order-table {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.order-header,
.order-row {
  display: grid;
  grid-template-columns:
    minmax(180px, 2.2fr)
    minmax(96px, 1fr)
    minmax(72px, 0.8fr)
    minmax(80px, 0.8fr)
    minmax(140px, 1.6fr)
    minmax(160px, 2fr)
    minmax(110px, 1fr)
    minmax(110px, 1fr)
    minmax(128px, 1fr);
  align-items: stretch;
}

.order-body {
  flex: 1;
  min-height: 0;
  max-height: calc(94vh - 180px);
  overflow-y: auto;
  overflow-x: auto;
}

.order-row {
  align-items: stretch;
  min-height: 72px;
}

.order-table .header-item,
.order-table .table-cell {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.order-table .text-left {
  justify-content: flex-start;
  text-align: left;
  line-height: 1.5;
  white-space: normal;
  word-break: break-word;
}

.col-address,
.col-event,
.col-repair {
  align-items: flex-start !important;
  padding-top: 12px;
  padding-bottom: 12px;
}

.col-time {
  font-size: 12px;
  line-height: 1.4;
}

.table-header,
.table-row {
  display: flex;
  align-items: center;
}

.table-header {
  background: rgba(0, 212, 255, 0.12);
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);
}

.header-item,
.table-cell {
  flex: 1;
  padding: 10px 12px;
  font-size: 13px;
  text-align: center;
  word-break: break-all;
}

.header-item {
  color: #00d4ff;
  font-weight: bold;
}

.table-row {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  &:hover {
    background: rgba(0, 212, 255, 0.06);
  }
}

.table-cell {
  color: #fff;
}

.flex-2 {
  flex: 2;
}

.image-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  align-items: center;
}

.table-image {
  width: 56px;
  height: 42px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid rgba(0, 212, 255, 0.3);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 8px rgba(0, 212, 255, 0.35);
  }
}

.empty-tip {
  padding: 28px;
  text-align: center;
  color: #88c4ff;
  font-size: 14px;
}

.pagination-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
}

.page-btn {
  padding: 6px 14px;
  background: rgba(0, 212, 255, 0.12);
  border: 1px solid rgba(0, 212, 255, 0.4);
  border-radius: 6px;
  color: #00d4ff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: rgba(0, 212, 255, 0.2);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-info {
  color: #88c4ff;
  font-size: 13px;
}

.tag-low {
  color: #66ccff;
}

.tag-medium {
  color: #ffcc00;
}

.tag-high {
  color: #ff9933;
}

.tag-urgent {
  color: #ff3366;
  font-weight: bold;
}

.status-pending {
  color: #ffcc00;
}

.status-processing {
  color: #00d4ff;
}

.status-done {
  color: #00ff88;
}

.image-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.image-preview-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
}

.preview-image {
  max-width: 100%;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid rgba(0, 212, 255, 0.4);
}

.close-preview-btn {
  position: absolute;
  top: -36px;
  right: 0;
  color: #00d4ff;
  font-size: 28px;
  cursor: pointer;
}
</style>
